import { useEventInfo } from '@shared/utils/eventInfo';

/** Params for useEventLocation hook */
type UseEventLocationParams = Readonly<{
  locationName?: Maybe<string>;
  locationFormat?: LocationFormat;
}>;

/**
 * Location format options for displaying the event location
 */
export const LOCATION_FORMAT_OPTIONS = {
  /** Format: venue name, city, state or country (e.g., "The Grand Ballroom, San Francisco, CA") */
  VENUE_CITY_STATE_COUNTRY: 'venueCityStateCountry',
  /** Format: city, state or country (e.g., "San Francisco, CA") */
  CITY_STATE_COUNTRY: 'cityStateCountry',
  /** Format: city only (e.g., "San Francisco") */
  CITY_ONLY: 'cityOnly',
} as const;

export type LocationFormat = (typeof LOCATION_FORMAT_OPTIONS)[keyof typeof LOCATION_FORMAT_OPTIONS];

/**
 * Format city, state or country (e.g., "San Francisco, CA")
 */
const formatCityStateCountry = (city?: string | null, state?: string | null, country?: string | null): string => {
  return [city, state || country].filter(Boolean).join(', ');
};

/**
 * Returns only the city/state/country parts that are NOT already present in the venue name.
 * This prevents duplication when the places API embeds city/state/country inside the venue name
 * (e.g., "Oregon State Fair, Salem, OR, USA" already contains "Salem" and "Oregon").
 */
const formatCityStateCountryNotInName = (name: string | null | undefined, city?: string | null, state?: string | null, country?: string | null): string => {
  const nameLower = (name ?? '').toLowerCase();
  const cityPart = city && !nameLower.includes(city.toLowerCase()) ? city : null;
  const regionPart = (() => {
    const region = state || country;
    if (!region) return null;
    return nameLower.includes(region.toLowerCase()) ? null : region;
  })();
  return [cityPart, regionPart].filter(Boolean).join(', ');
};

/**
 * Format location according to the specified format type
 */
export const formatLocationByType = (
  location: { name?: string | null; city?: string | null; state?: string | null; country?: string | null },
  formatType: LocationFormat,
): string => {
  const { name, city, state, country } = location;

  switch (formatType) {
    case LOCATION_FORMAT_OPTIONS.VENUE_CITY_STATE_COUNTRY: {
      // venue name, city, state or country — skip city/state already embedded in the name
      const cityStateCountry = formatCityStateCountryNotInName(name, city, state, country);
      return [name, cityStateCountry].filter(Boolean).join(', ');
    }
    case LOCATION_FORMAT_OPTIONS.CITY_STATE_COUNTRY: {
      // city, state or country
      return formatCityStateCountry(city, state, country);
    }
    case LOCATION_FORMAT_OPTIONS.CITY_ONLY: {
      // city only
      return city || '';
    }
    default:
      return name || '';
  }
};

/**
 * Derives a clean "City, State/Country" display string from structured location fields,
 * falling back to `name` when structured fields are unavailable.
 *
 * Works with both server-side locationInfo and client-side LocationInput selections.
 */
export const formatLocationDisplayValue = (location: { name?: string | null; city?: string | null; state?: string | null; country?: string | null }): string => {
  return formatLocationByType(location, LOCATION_FORMAT_OPTIONS.CITY_STATE_COUNTRY) || location.name || '';
};

/**
 * To determine the event's location information,
 * including a display-friendly name for use in UI.
 *
 * @returns An object containing:
 *   - `eventLocation`: The raw event location data.
 *   - `eventLocationDisplayName`: A human-readable location name for display.
 *   - `isLoadingEventLocation`: Boolean indicating if any of the data is still loading.
 */
export const useEventLocation = (params?: UseEventLocationParams) => {
  const { locationName, locationFormat } = params ?? {};
  const { eventInfo, loading: isLoadingEventLocation } = useEventInfo();

  const eventLocation = eventInfo?.locationInfo;

  // If locationFormat is provided, use the format function
  if (locationFormat && eventLocation) {
    const formattedLocation = formatLocationByType(eventLocation, locationFormat);
    return { eventLocation, eventLocationDisplayName: formattedLocation || eventLocation?.name, isLoadingEventLocation };
  }

  // Default: show "City, State" (or "City, Country" for international) for UI consistency
  const cityStateDisplay = formatCityStateCountry(eventLocation?.city, eventLocation?.state, eventLocation?.country);
  const eventLocationDisplayName = cityStateDisplay || locationName || eventLocation?.name;

  return { eventLocation, eventLocationDisplayName, isLoadingEventLocation };
};
